import { useEffect, useMemo, useState } from "react";
import { CopyButton, StateChip } from "../components/bits";
import { api, type MatchSummary } from "../lib/api";
import { describeProgress, fmtDuration, relativeTime, windowLabel } from "../lib/format";
import { Link } from "../lib/router";
import { useApp } from "../lib/store";

/** Fast poll while someone is actually staring at the pipeline. */
const DEBUG_POLL_MS = 2000;

const STATUS_ORDER: Record<string, number> = {
  watching: 0,
  discovered: 1,
  failed: 2,
  done: 3,
};

export function DebugPage({ windowId }: { windowId: string }) {
  const { status, refreshStatus, openMatch } = useApp();

  useEffect(() => {
    const t = setInterval(refreshStatus, DEBUG_POLL_MS);
    return () => clearInterval(t);
  }, [refreshStatus]);

  const win = status?.windows.find(w => w.event_window_id === windowId);
  const sessions = useMemo(
    () =>
      (status?.matches ?? [])
        .filter(m => m.event_window_id === windowId)
        .sort((a, b) => {
          const order = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
          if (order !== 0) return order;
          return a.session_id.localeCompare(b.session_id);
        }),
    [status, windowId],
  );
  const eventId = win?.event_id ?? sessions[0]?.event_id ?? "";

  const counts = {
    detected: sessions.length,
    parsing: sessions.filter(m => m.status === "watching").length,
    queued: sessions.filter(m => m.status === "discovered").length,
    done: sessions.filter(m => m.status === "done").length,
    failed: sessions.filter(m => m.status === "failed").length,
  };

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <Link
        to={`/window/${encodeURIComponent(windowId)}`}
        className="font-mono text-[11.5px] text-faint transition hover:text-dim"
      >
        ← window
      </Link>

      <header className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {counts.parsing > 0 ? (
              <StateChip tone="accent" pulse>
                pipeline live
              </StateChip>
            ) : (
              <StateChip tone="dim">pipeline idle</StateChip>
            )}
            {win && (
              <span className="font-mono text-[11px] text-faint">
                mode {win.mode}
                {win.discovery ? " · discovery on" : " · discovery off"}
                {win.backfilling && " · backfilling"}
              </span>
            )}
          </div>
          <h1 className="display mt-1.5 text-xl leading-tight text-fg sm:text-2xl">
            Pipeline
            <span className="text-dim">
              {" "}
              · {eventId ? windowLabel(windowId, eventId) : windowId}
            </span>
          </h1>
        </div>
        {status && (
          <span className="font-mono text-[11px] text-faint">
            permits {status.permits.in_use}/
            {status.permits.total > 0 ? status.permits.total : "∞"} in use globally
          </span>
        )}
      </header>

      <div className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-border/60 sm:grid-cols-5">
        <StatTile label="match ids detected" value={counts.detected} />
        <StatTile label="parsing now" value={counts.parsing} tone={counts.parsing > 0 ? "accent" : "fg"} />
        <StatTile label="queued" value={counts.queued} />
        <StatTile label="captured" value={counts.done} tone="ok" />
        <StatTile label="failed" value={counts.failed} tone={counts.failed > 0 ? "err" : "fg"} />
      </div>

      {sessions.length === 0 ? (
        <p className="mt-8 rounded-lg bg-raised/60 px-4 py-4 text-center font-mono text-[11.5px] text-faint">
          no session ids discovered yet — the first ones surface a few minutes into
          the window, via the history of teams that died early
        </p>
      ) : (
        <ol className="chamfer mt-5 divide-y divide-border/50 overflow-hidden bg-surface">
          {sessions.map(m => (
            <SessionRow
              key={m.session_id}
              m={m}
              windowId={windowId}
              eventId={eventId}
              onOpen={() => openMatch(m.session_id)}
            />
          ))}
        </ol>
      )}
    </main>
  );
}

function StatTile({
  label,
  value,
  tone = "fg",
}: {
  label: string;
  value: number;
  tone?: "fg" | "ok" | "err" | "accent";
}) {
  const tones = { fg: "text-fg", ok: "text-ok", err: "text-err", accent: "text-accent" };
  return (
    <span className="block bg-surface px-4 py-3">
      <span className={`block font-mono text-[18px] font-semibold ${tones[tone]}`}>{value}</span>
      <span className="label-xs block">{label}</span>
    </span>
  );
}

function SessionRow({
  m,
  windowId,
  eventId,
  onOpen,
}: {
  m: MatchSummary;
  windowId: string;
  eventId: string;
  onOpen: () => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const p = m.progress;

  const retry = async () => {
    if (retrying || !eventId) return;
    setRetrying(true);
    try {
      await api.parseSession(m.session_id, eventId, windowId);
    } catch {
      // the row keeps showing failed; nothing useful to add
    } finally {
      setRetrying(false);
    }
  };

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5">
      <span className="w-24 shrink-0">
        {m.status === "watching" && (
          <StateChip tone="accent" pulse>
            parsing
          </StateChip>
        )}
        {m.status === "discovered" && <StateChip tone="dim">queued</StateChip>}
        {m.status === "done" && <StateChip tone="ok">done</StateChip>}
        {m.status === "failed" && <StateChip tone="err">failed</StateChip>}
      </span>

      <button
        onClick={onOpen}
        title="open roster"
        className="flex min-w-0 shrink-0 items-center gap-1.5 font-mono text-[12px] text-fg transition hover:text-accent-bright"
      >
        {m.session_id.slice(0, 12)}…
      </button>
      <CopyButton text={m.session_id} label="copy" />

      <span className="min-w-0 flex-1">
        {m.status === "watching" && p && (
          <span className="flex items-center gap-3">
            <span className="shrink-0 font-mono text-[11px] text-accent">
              {describeProgress(p)}
            </span>
            <Meter done={p.data_chunks} total={p.data_total} />
            {p.event_total > 0 && <Meter done={p.event_chunks} total={p.event_total} />}
          </span>
        )}
        {m.status === "failed" && (
          <span className="font-mono text-[11px] text-err">{m.detail || "failed"}</span>
        )}
      </span>

      <span className="flex shrink-0 items-center gap-4 font-mono text-[11px] text-faint">
        {(p?.roster ?? m.player_count) > 0 && <span>{p?.roster ?? m.player_count} players</span>}
        {(p?.elims ?? m.elims ?? 0) > 0 && <span>{p?.elims ?? m.elims} elims</span>}
        {m.status === "watching" && p && (
          <span>
            {fmtDuration(p.elapsed_s)}
            {p.idle_s > 60 && <span className="text-warn"> · idle {fmtDuration(p.idle_s)}</span>}
          </span>
        )}
        {m.status === "done" && m.updated_at && <span>{relativeTime(m.updated_at)}</span>}
        {p?.live && <span className="text-ok">live</span>}
      </span>

      {m.status === "failed" && (
        <button
          onClick={retry}
          disabled={retrying}
          className="chamfer-sm shrink-0 bg-raised px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-dim transition hover:bg-overlay hover:text-fg"
        >
          {retrying ? "…" : "retry"}
        </button>
      )}
    </li>
  );
}

function Meter({ done, total }: { done: number; total: number }) {
  if (total <= 0) {
    return (
      <span className="relative h-1 w-24 overflow-hidden rounded-full bg-border">
        <span className="sweep-bar absolute inset-y-0 w-1/3 rounded-full bg-accent/60" />
      </span>
    );
  }
  return (
    <span className="h-1 w-24 overflow-hidden rounded-full bg-border">
      <span
        className="block h-full rounded-full bg-accent/80 transition-[width] duration-500"
        style={{ width: `${Math.round(Math.min(done / total, 1) * 100)}%` }}
      />
    </span>
  );
}
